import { useMemo } from 'react';
import Lottie from 'lottie-react';
import animationData from './loader.json';
import { DANTE_THEME_CHAT } from '@/constants';

/**
 * Converts hex color to RGB values
 * @param {string} hex - Hex color string (e.g., "#FF0000" or "FF0000")
 * @returns {Array} RGB values as array [r, g, b] normalized to 0-1 range
 */
const hexToRgb = (hex) => {
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  // Parse hex values
  const r = parseInt(cleanHex.substr(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substr(2, 2), 16) / 255;
  const b = parseInt(cleanHex.substr(4, 2), 16) / 255;
  
  return [r, g, b];
};

/**
 * DLoaderBubble - A Lottie-based loading component for Bubble and BubbleDirectLink
 * 
 * This component renders a circular progress loader with customizable element colors.
 * The loader is wrapped in a 64px container as requested.
 * 
 * @param {Object} props - Component props
 * @param {string} props.elementColor - The element color to use for the loader (hex format)
 * @param {string} props.size - Size of the loader container (default: 64px)
 * @returns {JSX.Element} The Lottie loader component
 */
const DLoaderBubble = ({ 
  elementColor = DANTE_THEME_CHAT.element_color, 
  size = 64 
}) => {
  // Modify the animation data to use the element color
  const modifiedAnimationData = useMemo(() => {
    if (!elementColor) return animationData;
    
    // Convert element color to RGB
    const [r, g, b] = hexToRgb(elementColor);
    
    // Create a deep copy of the animation data
    const modifiedData = JSON.parse(JSON.stringify(animationData));
    
    // Update the two active stroke layers with element color at different opacities
    // Layer 0: Upper stroke (full element color)
    // Layer 1: Lower stroke (element color with 70% opacity)
    // Layer 2: Background track (keep original light grey)
    
    if (modifiedData.layers) {
      // Layer 0 - Upper stroke (full element color)
      if (modifiedData.layers[0] && modifiedData.layers[0].shapes) {
        const upperLayer = modifiedData.layers[0];
        const upperStroke = upperLayer.shapes.find(shape => shape.nm === 'Stroke');
        if (upperStroke && upperStroke.c) {
          upperStroke.c.k = [r, g, b];
        }
      }
      
      // Layer 1 - Lower stroke (element color with 70% opacity)
      if (modifiedData.layers[1] && modifiedData.layers[1].shapes) {
        const lowerLayer = modifiedData.layers[1];
        const lowerStroke = lowerLayer.shapes.find(shape => shape.nm === 'Stroke');
        if (lowerStroke && lowerStroke.c) {
          // Apply element color with 70% opacity
          lowerStroke.c.k = [r * 0.7, g * 0.7, b * 0.7];
        }
      }
      
      // Layer 2 - Background track (keep original light grey - no changes)
      // This layer remains unchanged to maintain the light grey background
    }
    
    return modifiedData;
  }, [elementColor]);

  return (
    <div 
      className="flex items-center justify-center"
      style={{ 
        width: `${size}px`, 
        height: `${size}px` 
      }}
    >
      <Lottie
        animationData={modifiedAnimationData}
        loop={true}
        autoplay={true}
        style={{ 
          width: `${size}px`, 
          height: `${size}px` 
        }}
      />
    </div>
  );
};

export default DLoaderBubble;
